import { FC, useState } from 'react';
import {
  Grid,
  Typography,
  Card,
  CardContent,
  Button,
  Box,
  CircularProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import { Add } from '@mui/icons-material';
import { useTranslation } from 'src/hooks/useTranslationHook';
import { useInstituteLeavesHook } from '../../hooks/useInstituteLeavesHook';
import {
  useProfileLeavesStatsHook,
  useProfileLeavesListHook,
  useProfileApplyLeaveHook,
} from '../../hooks/useProfileLeavesHook';
import ApplyLeaveForm, { ApplyLeaveSubmitPayload } from './ApplyLeaveForm.view';
import dayjs from 'dayjs';

const ProfileLeavesView: FC = () => {
  const { translateText } = useTranslation();
  const [applyModalOpen, setApplyModalOpen] = useState(false);

  const { data: instituteLeaves, isLoading: instituteLoading } = useInstituteLeavesHook();
  const { data: statsData, isLoading: statsLoading } = useProfileLeavesStatsHook();
  const { data: leavesList = [], isLoading: listLoading } = useProfileLeavesListHook();
  const applyMutation = useProfileApplyLeaveHook();

  const leaveTypes = instituteLeaves?.leaveTypes ?? [];
  const stats = statsData?.stats ?? [];

  const handleApplySubmit = (payload: ApplyLeaveSubmitPayload) => {
    applyMutation.mutate(payload, {
      onSuccess: () => setApplyModalOpen(false),
    });
  };

  const isLoading = instituteLoading || statsLoading;

  return (
    <Grid container spacing={3} sx={{ p: 2 }} data-testid="profile-leaves-container">
      <Grid item xs={12}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
          }}
          data-testid="profile-leaves-header"
        >
          <Typography variant="h6">{translateText('profile.leaves.title')}</Typography>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setApplyModalOpen(true)}
            data-testid="profile-leaves-apply-cta"
          >
            {translateText('profile.leaves.applyLeave')}
          </Button>
        </Box>
      </Grid>

      {isLoading ? (
        <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress data-testid="profile-leaves-loading" />
        </Grid>
      ) : (
        <>
          <Grid item xs={12}>
            <Typography variant="subtitle2" gutterBottom>
              {translateText('profile.leaves.balanceTitle')}
            </Typography>
            <Grid container spacing={2}>
              {stats.map((s) => (
                <Grid item xs={12} sm={6} md={3} key={s.leaveTypeId}>
                  <Card data-testid={`profile-leaves-balance-card-${s.leaveTypeCode}`}>
                    <CardContent>
                      <Typography variant="body2" color="text.secondary">
                        {s.leaveTypeName}
                      </Typography>
                      <Typography variant="h6">
                        {s.available} / {s.available + s.taken}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {translateText('profile.leaves.available')}: {s.available},{' '}
                        {translateText('profile.leaves.taken')}: {s.taken}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
              {translateText('profile.leaves.historyTitle')}
            </Typography>
            <TableContainer component={Paper} data-testid="profile-leaves-history-table">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{translateText('profile.leaves.leaveType')}</TableCell>
                    <TableCell>{translateText('profile.leaves.startDate')}</TableCell>
                    <TableCell>{translateText('profile.leaves.endDate')}</TableCell>
                    <TableCell>{translateText('profile.leaves.status')}</TableCell>
                    <TableCell>{translateText('profile.leaves.reason')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {listLoading ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        <CircularProgress size={24} />
                      </TableCell>
                    </TableRow>
                  ) : leavesList.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        {translateText('profile.leaves.noLeaves')}
                      </TableCell>
                    </TableRow>
                  ) : (
                    leavesList.map((leave: { id: string; leaveTypeName: string; startDate: string; endDate: string; status: string; reason?: string }) => (
                      <TableRow key={leave.id}>
                        <TableCell>{leave.leaveTypeName}</TableCell>
                        <TableCell>{dayjs(leave.startDate).format('DD MMM YYYY')}</TableCell>
                        <TableCell>{dayjs(leave.endDate).format('DD MMM YYYY')}</TableCell>
                        <TableCell>
                          <Chip
                            label={leave.status}
                            size="small"
                            color={
                              leave.status === 'approved'
                                ? 'success'
                                : leave.status === 'rejected'
                                  ? 'error'
                                  : 'default'
                            }
                          />
                        </TableCell>
                        <TableCell>{leave.reason ?? '-'}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        </>
      )}

      <ApplyLeaveForm
        isOpen={applyModalOpen}
        onClose={() => setApplyModalOpen(false)}
        onSubmit={handleApplySubmit}
        leaveTypes={leaveTypes}
        isLoading={applyMutation.isPending}
        errorMessage={applyMutation.error?.message}
      />
    </Grid>
  );
};

export default ProfileLeavesView;
